import { useContext, useCallback } from 'react';
import _get from 'lodash/get';
import _has from 'lodash/has';
import { formatListData, AjaxContext, AppContext, EMPTY_STRING, EMPTY_ARRAY, EHttpStatus, ERROR_SEVERITY } from 'commons-ui';
import { useLocation } from 'react-router-dom';
import { EFrequencyType, JOBS_ENDPOINTS, JOB_NAME, GAME_NAME } from 'constant';
import { Record } from './helper';

function formatUserName(email: string): string {
	// Validate email input
	if (!email || !email.includes('@')) {
		const errorMessage: string = 'Invalid email: must not be empty and must contain "@" symbol';
		// Create a mock error object to simulate the response structure
		const error: { response: { status: number; data: { message: string } } } = {
			response: {
				status: EHttpStatus.CODE_422,
				data: {
					message: errorMessage
				}
			}
		} as const; // Use 'as const' to assert the type
		throw error; // Throw the constructed error object
	}
	// Split the email to get the username part
	const username: string = email.split('@')[0];

	// Split the username by '.' to get individual name parts
	const nameParts: string[] = username.split('.');

	// Capitalize the first letter of each part and join them with a space
	const formattedName: string = nameParts.map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');

	return formattedName;
}

const useService = () => {
	const { postData, getData } = useContext(AjaxContext);
	const {
		app: { appName }
	} = useContext(AppContext);
	const location = useLocation();
	const queryParams = new URLSearchParams(location.search);
	const zupeeUserId: string = queryParams.get('zupeeUserId') || EMPTY_STRING;

	const getErrorMessage = (error: any): string => {
		try {
			let firstIndex: number = error.response.data.error.indexOf('-');
			const errorObject = JSON.parse(error.response.data.error.slice(firstIndex + 1).trim()).error;
			firstIndex = errorObject.indexOf('-');
			const errorMessage: string = JSON.stringify(JSON.parse(errorObject.slice(firstIndex + 1).trim())?.data);
			return errorMessage;
		} catch (e: any) {
			return 'Something went wrong';
		}
	};

	const requestGetSegmentList = useCallback(
		async ({
			pageDetails,
			searchText,
			includeDraft = true
		}: {
			pageDetails: { pageNo: number; pageSize: number };
			searchText: string;
			includeDraft?: boolean;
		}) => {
			const pageNo: number = pageDetails.pageNo || 1;
			const pageSize: number = pageDetails.pageSize || 20;
			const skipCount: number = (pageNo - 1) * pageSize;
			const result = await postData({
				endpoint: '/marketing/dashboard/userSegment/getSegmentList',
				queryParams: {
					limit: pageSize,
					skip: skipCount,
					appName,
					searchText,
					...(includeDraft && { includeDraft: true })
				}
			});
			return {
				rowCount: _get(result, ['data', 'totalCount'], 0),
				data: formatListData(_get(result, ['data', 'data'], EMPTY_ARRAY))
			};
		},
		EMPTY_ARRAY
	);

	const requestDeleteSegment = useCallback(async (rowData: Record, showAlert: any, user: any, byPassChecks: boolean = false) => {
		try {
			const recordId = rowData._id;
			const response = await postData({
				endpoint: `/marketing/dashboard/userSegment/deleteSegment`,
				queryParams: {
					recordId,
					appName
				},
				body: {
					shouldBypassChecks: byPassChecks,
					segmentId: rowData.segmentId,
					segmentType: rowData.SegmentType,
					deletedBy: user.email
				}
			});
			if (response.status !== 200) {
				showAlert(response.data.message || 'Error occurred while deleting segment', { type: 'error' });
				return;
			}
			showAlert('Segment deleted successfully');
		} catch (error: any) {
			showAlert(getErrorMessage(error) || 'Error occurred while deleting segment', { type: 'error' });
		}
	}, EMPTY_ARRAY);

	const requestUpdateSegment = useCallback(async (updatedSegmentInfo: Record, showAlert: any) => {
		try {
			const recordId = updatedSegmentInfo._id;
			const updatedSegmentInfoNew: Record = { ...updatedSegmentInfo };
			if (_has(updatedSegmentInfoNew, '_id')) {
				delete updatedSegmentInfoNew._id;
			}
			const response = await postData({
				endpoint: `/marketing/dashboard/userSegment/updateSegment`,
				body: updatedSegmentInfoNew,
				queryParams: {
					recordId,
					appName
				}
			});
			if (response.status !== 200) {
				showAlert(response.data.message || 'Error occurred while updating segment', { type: 'error' });
				return false;
			}
			showAlert('Segment updated successfully');
		} catch (error: any) {
			let reloadPage: boolean = false;
			let errorMessage: string = EMPTY_STRING;
			if (error.response.status !== 500) {
				errorMessage = getErrorMessage(error);
			} else {
				errorMessage =
					'Processing is taking too much time. You will receive a notification on Slack in case of success or failure';
				reloadPage = true;
			}
			showAlert(errorMessage || 'Error occurred while updating segment', { type: 'error' });
			return reloadPage;
		}
		return true;
	}, EMPTY_ARRAY);

	const requestActivateSegment = useCallback(async (updatedSegmentInfo: Record, showAlert: any, user: any) => {
		try {
			const recordId = updatedSegmentInfo._id;
			const response = await postData({
				endpoint: `/marketing/dashboard/userSegment/makeSegmentLive`,
				queryParams: {
					appName
				},
				body: {
					id: recordId,
					app: appName,
					jobCreatedBy: formatUserName(user.email),
					isRapid: updatedSegmentInfo.isRapid,
					jobName: JOB_NAME,
					name: `${updatedSegmentInfo.name} Job`
				}
			});
			if (response.status !== EHttpStatus.CODE_200 || !response.data.message.includes('success')) {
				showAlert(response.data?.message || 'Error occurred while activating segment');
				return false;
			}
			showAlert('Segment successfully activated');
		} catch (error: any) {
			let reloadPage: boolean = false;
			let errorMessage: string = EMPTY_STRING;
			if (error.response.status !== EHttpStatus.CODE_500) {
				switch (error.response.status) {
					case 422:
						errorMessage = error.response.data.message;
						break;
					default:
						errorMessage = getErrorMessage(error);
						break;
				}
			} else {
				errorMessage = 'Processing is taking too much time in activating segment';
				reloadPage = true;
			}
			showAlert(errorMessage || 'Error occurred while activating segment', { type: 'error' });
			return reloadPage;
		}
		return true;
	}, EMPTY_ARRAY);

	const requestGetSegment = useCallback(async () => {
		const response = await postData({
			endpoint: `/marketing/dashboard/userSegment/getUserSegment/${zupeeUserId}`,
			queryParams: {
				appName
			}
		});
		return {
			rowCount: formatListData(_get(response, ['data', 'data'], EMPTY_ARRAY)).length,
			data: formatListData(_get(response, ['data', 'data'], EMPTY_ARRAY))
		};
	}, EMPTY_ARRAY);

	const requestGetRtfs = useCallback(async () => {
		const response = await getData({
			endpoint: `/marketing/dashboard/userSegment/getUserRtfs`,
			queryParams: {
				appName,
				zupeeUserId
			}
		});
		return {
			data: formatListData(_get(response, ['data', 'data'], EMPTY_ARRAY)),
			jsonData: response.data.data,
			zupeeUserId
		};
	}, EMPTY_ARRAY);

	const requestUserData = useCallback(async (selectionType: string, selectedValue: string | number) => {
		const result = await postData({
			endpoint: 'marketing/dashboard/userSegment/getUsers',
			queryParams: {
				appName
			},
			body: {
				[selectionType || EMPTY_STRING]: selectedValue
			}
		});
		const data = _get(result, ['data', 'data'], {});
		return data;
	}, EMPTY_ARRAY);

	const requestUpdateUserSegment = useCallback(
		async (userId: string, userSegments: string, toBeDeleted: boolean, showAlert: any) => {
			try {
				const segmentNamesList: string[] = userSegments
					.split(',')
					.map((segment) => segment.trim())
					.filter((segment) => segment !== EMPTY_STRING);
				if (segmentNamesList.length === 0) {
					showAlert('Please enter non-empty string as segment name', { type: 'error' });
					return false;
				}
				const response = await postData({
					endpoint: `/marketing/dashboard/userSegment/updateUserSegment/${userId}`,
					queryParams: {
						toBeDeleted,
						appName
					},
					body: {
						segmentNames: segmentNamesList
					}
				});
				if (response.status !== 200) {
					showAlert(response.data.message || 'Error occurred while updating user segment', { type: 'error' });
					return false;
				}
				showAlert(response.data.message);
			} catch (error: any) {
				showAlert(getErrorMessage(error) || 'Error occurred while updating user segment', { type: 'error' });
				return false;
			}
			return true;
		},
		EMPTY_ARRAY
	);

	const requestUpdateUserSegmentToVip = useCallback(async (userId: number, addToStatic: boolean, showAlert: any) => {
		try {
			// eslint-disable-next-line no-console
			console.log('userId', userId);
			const response = await postData({
				endpoint: `/marketing/dashboard/userSegment/updateUserSegmentToVip`,
				queryParams: {
					addToStatic,
					appName
				},
				body: {
					zupeeUserId: userId
				}
			});
			if (response.status !== 200) {
				showAlert(response.data.message || 'Error occurred while updating user to vip', { type: 'error' });
				return false;
			}
			showAlert(response.data.message);
		} catch (error: any) {
			showAlert(getErrorMessage(error) || 'Error occurred while updating user segment to vip', { type: 'error' });
			return false;
		}
		return true;
	}, EMPTY_ARRAY);

	const requestCreateSegment = useCallback(async (newSegmentInfo: Record, showAlert: any) => {
		try {
			const updatedNewSegmentInfo: Record = { ...newSegmentInfo };
			const response = await postData({
				endpoint: `/marketing/dashboard/userSegment/createSegment`,
				body: updatedNewSegmentInfo,
				queryParams: {
					appName
				}
			});
			if (response.status !== 200) {
				showAlert(response.data.message || 'Error occurred while creating segment', { type: 'error' });
				return false;
			}
			showAlert('Segment created successfully');
		} catch (error: any) {
			let reloadPage: boolean = false;
			let errorMessage: string = EMPTY_STRING;
			if (error.response.status !== 500) {
				errorMessage = getErrorMessage(error);
			} else {
				errorMessage =
					'Processing is taking too much time. You will receive a notification on Slack in case of success or failure';
				reloadPage = true;
			}
			showAlert(errorMessage || 'Error occurred while creating segment', { type: 'error' });
			return reloadPage;
		}
		return true;
	}, EMPTY_ARRAY);

	const requestGetPlatformFeatures = useCallback(
		async ({ pageDetails, searchText }: { pageDetails: { pageNo: number; pageSize: number }; searchText: string }) => {
			const pageNo: number = pageDetails.pageNo || 1;
			const pageSize: number = pageDetails.pageSize || 20;
			const skipCount: number = (pageNo - 1) * pageSize;
			const response = await postData({
				endpoint: `/marketing/dashboard/generic/adminServiceApi`,
				queryParams: {
					appName
				},
				body: {
					reqMethod: `POST`,
					adminPath: `userSegment/getPlatformFeatures?limit=#&skip=#&searchText=#`,
					pathParams: [pageSize, skipCount, searchText]
				}
			});
			return {
				data: formatListData(_get(response, ['data', 'data', 'platformFeatures'], EMPTY_ARRAY)),
				rowCount: response?.data?.data?.totalCount || 0
			};
		},
		EMPTY_ARRAY
	);

	const requestUpdatePlatformFeature = useCallback(async (newPlatformFeaturesData: Record, showAlert: any) => {
		try {
			const response = await postData({
				endpoint: `/marketing/dashboard/generic/adminServiceApi`,
				queryParams: {
					appName
				},
				body: {
					reqMethod: `POST`,
					adminPath: `userSegment/updatePlatformFeature`,
					pathParams: [],
					...newPlatformFeaturesData
				}
			});
			if (response.status !== EHttpStatus.CODE_200) {
				showAlert(response.data.message || 'Error occurred while updating platform feature', { type: 'error' });
				return false;
			}
			showAlert('Platform feature updated successfully');
		} catch (error: any) {
			showAlert(getErrorMessage(error) || 'Error occurred while updating platform feature', { type: 'error' });
			return false;
		}
		return true;
	}, EMPTY_ARRAY);

	const requestGetFeatureSegmentsList = useCallback(
		async ({ pageDetails, searchText }: { pageDetails: { pageNo: number; pageSize: number }; searchText: string }) => {
			const pageNo: number = pageDetails.pageNo || 1;
			const pageSize: number = pageDetails.pageSize || 20;
			const skipCount: number = (pageNo - 1) * pageSize;
			const response = await postData({
				endpoint: `/marketing/dashboard/generic/adminServiceApi`,
				queryParams: {
					appName
				},
				body: {
					reqMethod: `POST`,
					adminPath: `userSegment/getFeatureSegmentsList?limit=#&skip=#&searchText=#`,
					pathParams: [pageSize, skipCount, searchText]
				}
			});
			return {
				data: formatListData(_get(response, ['data', 'data', 'featureSegments'], EMPTY_ARRAY)),
				rowCount: _get(response, ['data', 'totalCount'], 0)
			};
		},
		EMPTY_ARRAY
	);

	const requestUpdateFeatureSegments = useCallback(async (newFeatureSegmentData: Record, showAlert: any) => {
		try {
			const response = await postData({
				endpoint: `/marketing/dashboard/generic/adminServiceApi`,
				queryParams: {
					appName
				},
				body: {
					reqMethod: `POST`,
					adminPath: `userSegment/updateFeatureSegments`,
					pathParams: [],
					...newFeatureSegmentData
				}
			});
			if (response.status !== EHttpStatus.CODE_200) {
				showAlert(response.data.message || 'Error occurred while updating feature segment', { type: 'error' });
				return false;
			}
			showAlert('Feature segment updated successfully');
		} catch (error: any) {
			showAlert(getErrorMessage(error) || 'Error occurred while updating feature segment', { type: 'error' });
			return false;
		}
		return true;
	}, EMPTY_ARRAY);

	const requestCreateBulkSegments = useCallback(async (newBulkData: Record) => {
		const updatedBulkData = {
			...newBulkData,
			createdAt: new Date().getTime(),
			processingStatus: 'processing',
			expiryTime: newBulkData.expiryTime
		};
		if (newBulkData?.toBeDeleted) {
			delete updatedBulkData.expiryTime;
		}
		return postData({
			endpoint: '/marketing/dashboard/userSegment/assignBulkSegments',
			body: updatedBulkData,
			customHeaders: { 'Content-Type': 'multipart/form-data' },
			queryParams: {
				appName
			}
		});
	}, []);

	const requestGetBulkSegmetsData = useCallback(
		async ({ pageDetails, searchText }: { pageDetails: { pageNo: number; pageSize: number }; searchText: string }) => {
			const pageNo: number = pageDetails.pageNo || 1;
			const pageSize: number = pageDetails.pageSize || 20;
			const skipCount: number = (pageNo - 1) * pageSize;
			const result = await getData({
				endpoint: '/marketing/dashboard/userSegment/getBulkSegmentData',
				queryParams: {
					limit: pageSize,
					skip: skipCount,
					appName,
					searchText
				}
			});
			const data = result?.data;
			const listData = formatListData(data.data);
			return { rowCount: data.totalCount, data: listData };
		},
		[]
	);

	const requestRunSegment = useCallback(async (rowData: Record, showAlert: any, user: any) => {
		try {
			const { segmentId } = rowData;
			if (!segmentId) {
				showAlert('Segment ID not found', { type: ERROR_SEVERITY.ERROR });
				return false;
			}
			const response: { data: { success: boolean; message: string }; status: number } = await postData({
				endpoint: JOBS_ENDPOINTS.EXECUTE_SEGMENT,
				queryParams: {
					appName
				},
				body: {
					jobName: JOB_NAME,
					gameName: GAME_NAME,
					segmentToExecute: segmentId.toString(),
					prevJobExecId: EMPTY_STRING,
					isRapid: _get(rowData, 'isRapid', false),
					jobTriggeredBy: user.email
				}
			});

			if (response.data.success === false || response.status !== EHttpStatus.CODE_200) {
				showAlert(response.data.message || 'Error occurred while triggering segment', { type: ERROR_SEVERITY.ERROR });
				return false;
			}

			showAlert('Segment triggered successfully');
			return true;
		} catch (error: any) {
			showAlert(getErrorMessage(error) || 'Error occurred while triggering segment', { type: ERROR_SEVERITY.ERROR });
			return false;
		}
	}, EMPTY_ARRAY);

	const deleteScheduledCronJob = async (rowData: Record, showAlert: any) => {
		try {
			const response: { data: { success: boolean; message: string }; status: number } = await postData({
				endpoint: JOBS_ENDPOINTS.DELETE_SCHEDULED_CRON_JOB,
				queryParams: {
					appName,
					segmentToExecute: rowData.segmentId.toString(),
					jobName: JOB_NAME
				}
			});
			if (response.data.success === false || response.status !== EHttpStatus.CODE_200) {
				showAlert(response.data.message || 'Issue occurred while deleting Scheduled Cron Job', {
					type: ERROR_SEVERITY.ERROR
				});
				return false;
			}
			showAlert('Scheduled Cron Job Deleted successfully');
			return true;
		} catch (error: any) {
			showAlert(getErrorMessage(error) || 'Error occurred while deleting Scheduled Cron Job', { type: ERROR_SEVERITY.ERROR });
			return false;
		}
	};

	const requestScheduleSegment = useCallback(
		async (rowData: Record, showAlert: any, user: any): Promise<boolean> => {
			try {
				if (rowData.deleteCronJob) {
					return deleteScheduledCronJob(rowData, showAlert);
				}
				const scheduleTimeStr: string = String(rowData.scheduleTime);
				const [hours, minutes]: number[] = scheduleTimeStr.split(':').map(Number);
				let cronExpression: string = '';
				const scheduleDate: Date | string =
					rowData.scheduleFrequency === EFrequencyType.ONCE ? new Date(Number(rowData.scheduleDate)) : '';
				switch (rowData.scheduleFrequency) {
					case EFrequencyType.ONCE:
						if (scheduleDate instanceof Date) {
							cronExpression = `${minutes} ${hours} ${scheduleDate.getDate()} ${scheduleDate.getMonth() + 1} *`;
						}
						break;
					case EFrequencyType.WEEKLY:
						cronExpression = `${minutes} ${hours} * * ${rowData.scheduleWeekDay}`;
						break;
					case EFrequencyType.DAILY:
						cronExpression = `${minutes} ${hours} * * *`;
						break;
					default:
						throw new Error('Invalid schedule frequency');
				}

				const response: { data: { success: boolean; message: string }; status: number } = await postData({
					endpoint: JOBS_ENDPOINTS.SCHEDULE_SEGMENT,
					body: {
						segmentToExecute: rowData.segmentId.toString(),
						jobName: JOB_NAME,
						cronValue: cronExpression,
						isRapid: _get(rowData, 'isRapid', false),
						scheduledDatetimestamp: rowData.scheduleDate,
						jobScheduledBy: user.email
					},
					queryParams: {
						appName
					}
				});
				if (response.data.success === false || response.status !== EHttpStatus.CODE_200) {
					showAlert(response.data.message || 'Issue occurred while scheduling Cron Job', { type: ERROR_SEVERITY.ERROR });
					return false;
				}
				showAlert(`Segment scheduled successfully | Frequency:${rowData.scheduleFrequency} At: ${scheduleTimeStr}`);
				return true;
			} catch (error) {
				showAlert(getErrorMessage(error) || 'Issue occurred while scheduling Cron Job', { type: ERROR_SEVERITY.ERROR });
				return false;
			}
		},
		[appName]
	);

	// eslint-disable-next-line @typescript-eslint/no-shadow
	const requestCheckUserLiveSegment = useCallback(async (zupeeUserId: string, segmentName: string) => {
		const response = await postData({
			endpoint: `/marketing/dashboard/userSegment/checkUserLiveSegment`,
			queryParams: {
				appName
			},
			body: {
				zupeeUserId,
				segmentName
			}
		});
		return _get(response, ['data'], {});
	}, EMPTY_ARRAY);

	return {
		requestGetSegmentList,
		requestDeleteSegment,
		requestUpdateSegment,
		requestGetSegment,
		requestUserData,
		requestUpdateUserSegment,
		requestCreateSegment,
		requestUpdateUserSegmentToVip,
		requestGetRtfs,
		requestGetPlatformFeatures,
		requestUpdatePlatformFeature,
		requestGetFeatureSegmentsList,
		requestUpdateFeatureSegments,
		requestCreateBulkSegments,
		requestGetBulkSegmetsData,
		requestActivateSegment,
		requestRunSegment,
		requestScheduleSegment,
		requestCheckUserLiveSegment
	};
};

export default useService;
